import { JobProgressConsole } from '@/components/JobProgressConsole';
import { Badge, Button } from '@/components/ui';
import { useBackgroundJob } from '@/hooks/useBackgroundJob';
import {
  sysadminService,
  type AccionMigrarHorasDto,
  type DetalleMigracionItemDto,
  type EstadoMigrarHorasServidorDto,
  type ResumenMigrarHorasDto,
} from '@/services/sysadmin.service';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  FileAudio,
  FileWarning,
  HelpCircle,
  Info,
  Loader2,
  Play,
  RefreshCw,
  Search,
  ShieldAlert,
  Type,
  Upload,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const POLL_ESTADO_MS = 8_000;

export function AplicarHorasActasPanel() {
  const [empresaIdsInput, setEmpresaIdsInput] = useState('');
  const [limitInput, setLimitInput] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [errorPreview, setErrorPreview] = useState<string | null>(null);
  const [resumenPreview, setResumenPreview] = useState<ResumenMigrarHorasDto | null>(null);
  const [estadoServidor, setEstadoServidor] = useState<EstadoMigrarHorasServidorDto | null>(null);
  const [cargandoEstado, setCargandoEstado] = useState(true);
  const [filtroTabla, setFiltroTabla] = useState<string>('todos');
  const [busquedaTabla, setBusquedaTabla] = useState('');
  /** Asesorías importadas desde un JSON: si hay, la corrida se limita a ellas. */
  const [asesoriaIdsImportadas, setAsesoriaIdsImportadas] = useState<number[] | null>(null);
  const inputImportarRef = useRef<HTMLInputElement>(null);

  const { isProcessing, isLoadingMore, hasMore, progress, summary, logs, startJob, connectOnly, loadMoreLogs } = useBackgroundJob(
    '/api/sysadmin/actas/aplicar-horas',
    '/api/system-jobs/stream',
    { service: 'acta-aplicar-horas' }
  );

  const empresaIds = useMemo(
    () =>
      empresaIdsInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [empresaIdsInput]
  );

  const limit = limitInput.trim() ? Number(limitInput.trim()) : undefined;

  // Sincronizar estado del servidor en segundo plano
  const yaReconectado = useRef(false);
  const sincronizarEstado = useCallback(async () => {
    try {
      const { estado, ultimaEjecucion } = await sysadminService.obtenerEstadoMigracionHoras();
      setEstadoServidor(estado);
      if (ultimaEjecucion && !resumenPreview) {
        setResumenPreview(ultimaEjecucion);
      }
      if (estado?.enCurso && !yaReconectado.current) {
        yaReconectado.current = true;
        connectOnly();
      }
      if (!estado?.enCurso) yaReconectado.current = false;
    } catch {
      // Ignorar errores de sondeo
    } finally {
      setCargandoEstado(false);
    }
  }, [connectOnly, resumenPreview]);

  useEffect(() => {
    void sincronizarEstado();
    const id = setInterval(() => void sincronizarEstado(), POLL_ESTADO_MS);
    return () => clearInterval(id);
  }, [sincronizarEstado]);

  // La previsualización recorre todas las actas en Drive y tarda más que el timeout de una petición,
  // así que corre en el servidor como simulación (solo lectura) y se sigue por SSE.
  const handlePrevisualizar = () => handleIniciarBatch(false);

  const handleIniciarBatch = async (aplicarReal: boolean, accion: AccionMigrarHorasDto = 'AGREGAR_AUDIOS') => {
    setErrorPreview(null);
    await startJob({
      ...(empresaIds.length ? { empresaIds } : {}),
      ...(asesoriaIdsImportadas?.length ? { asesoriaIds: asesoriaIdsImportadas } : {}),
      ...(limit ? { limit } : {}),
      dryRun: !aplicarReal,
      accion,
    });
    void sincronizarEstado();
  };

  // Exporta lo que se ve en la tabla (filtro + búsqueda) para volver a correr solo esas actas después.
  const handleExportar = () => {
    const contenido = {
      exportadoEn: new Date().toISOString(),
      filtro: filtroTabla,
      asesoriaIds: detallesFiltrados.map((d) => d.asesoriaId),
      detalles: detallesFiltrados,
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(contenido, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `actas-horas-${filtroTabla}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Acepta el JSON exportado ({ asesoriaIds } o { detalles }) o una lista simple de IDs.
  const handleImportar = async (archivo: File) => {
    setErrorPreview(null);
    try {
      const data: unknown = JSON.parse(await archivo.text());
      const crudos: unknown[] = Array.isArray(data)
        ? data
        : ((data as { asesoriaIds?: unknown[] }).asesoriaIds ?? (data as { detalles?: unknown[] }).detalles ?? []);
      const ids = [
        ...new Set(
          crudos
            .map((x) => (typeof x === 'object' && x !== null ? (x as { asesoriaId?: unknown }).asesoriaId : x))
            .map(Number)
            .filter((n) => Number.isInteger(n) && n > 0)
        ),
      ];
      if (ids.length === 0) throw new Error('El archivo no tiene IDs de asesoría');
      setAsesoriaIdsImportadas(ids);
    } catch (err: any) {
      setErrorPreview(`No se pudo importar el archivo: ${err.message}`);
    } finally {
      if (inputImportarRef.current) inputImportarRef.current.value = '';
    }
  };

  const [cancelando, setCancelando] = useState(false);
  const handleCancelarSimulacion = async () => {
    setCancelando(true);
    try {
      await sysadminService.cancelarSimulacionHoras();
      await sincronizarEstado();
    } catch (err: any) {
      setErrorPreview(err.message || 'No se pudo cancelar la simulación');
    } finally {
      setCancelando(false);
    }
  };

  const enCurso = estadoServidor?.enCurso === true;
  const resumenActual = (summary as ResumenMigrarHorasDto | null) ?? resumenPreview;

  const detallesFiltrados = useMemo(() => {
    if (!resumenActual?.detalles) return [];
    return resumenActual.detalles.filter((item) => {
      if (filtroTabla === 'elegibles' && item.estado !== 'APLICADA_OK' && item.estado !== 'DRY_RUN_OK') return false;
      if (filtroTabla === 'problemas' && item.estado !== 'SALTADA_PROBLEMA_AUDIOS') return false;
      if (filtroTabla === 'ya_tienen' && item.estado !== 'SALTADA_YA_TIENE_AUDIOS') return false;
      if (filtroTabla === 'no_transformadas' && item.estado !== 'SALTADA_NO_TRANSFORMADA') return false;
      if (filtroTabla === 'errores' && item.estado !== 'ERROR') return false;
      if (filtroTabla === 'letra' && item.estado !== 'LETRA_POR_CORREGIR' && item.estado !== 'LETRA_CORREGIDA') return false;

      if (busquedaTabla.trim()) {
        const q = busquedaTabla.toLowerCase();
        return (
          item.asesoriaId.toString().includes(q) ||
          item.empresaNombre.toLowerCase().includes(q) ||
          item.empresaNit.toLowerCase().includes(q) ||
          item.asesorNombre.toLowerCase().includes(q) ||
          (item.docTitulo && item.docTitulo.toLowerCase().includes(q)) ||
          (item.motivo && item.motivo.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [resumenActual, filtroTabla, busquedaTabla]);

  return (
    <div className="space-y-6">
      {/* Banner explicativo con las 2 condiciones */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/80 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="space-y-1.5 text-sm text-blue-900">
            <h2 className="font-semibold text-blue-950">Funcionalidad de Aplicación de Fila AUDIOS (Horas) a Actas ya Transformadas</h2>
            <p className="text-blue-800">
              Inserta directamente la fila <strong>AUDIOS</strong> (tabla interna con nombres de archivo, duración y total) en los
              documentos de Google Docs existentes, <strong>preservando todas las ediciones manuales</strong> realizadas al texto.
            </p>
            <div className="pt-1 text-xs text-blue-900 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="bg-white/80 rounded-lg p-2.5 border border-blue-200">
                <span className="font-semibold block text-blue-950 mb-0.5">1. Solo Actas ya Transformadas</span>
                Aplica únicamente a actas cuyo título inicia con{' '}
                <code className="font-mono bg-blue-100 px-1 py-0.5 rounded">AAAAMMDD_</code> (las que ya fueron migradas al nuevo formato de
                plantilla). Las actas viejas sin transformar no se tocan.
              </div>
              <div className="bg-white/80 rounded-lg p-2.5 border border-blue-200">
                <span className="font-semibold block text-blue-950 mb-0.5">2. Filtro de Seguridad de Audios</span>
                Omite automáticamente cualquier asesoría donde <strong>algún audio vigente lo subió alguien distinto al dueño</strong>{' '}
                (todos de otro asesor o mixtos). Solo se procesan las asesorías con todos sus audios del dueño o sin audios.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerta de ejecución en curso en el servidor */}
      {enCurso && estadoServidor && (
        <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3.5">
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-blue-600" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">
              Hay una corrida de migración de horas en curso ({estadoServidor.dryRun ? 'Simulación' : 'Aplicación Real'})
            </p>
            <p className="mt-0.5 text-blue-800">
              Iniciada a las {new Date(estadoServidor.iniciadoEn).toLocaleTimeString('es-CO')} ·{' '}
              <span className="tabular-nums">
                {estadoServidor.procesadas} de {estadoServidor.total}
              </span>{' '}
              actas procesadas · <span className="tabular-nums font-semibold">{estadoServidor.aplicadas} elegibles/aplicadas</span>
              {estadoServidor.fallidas > 0 && <span className="tabular-nums"> · {estadoServidor.fallidas} con error</span>}
              {estadoServidor.asesoriaActual !== null && <span> · asesoría actual: #{estadoServidor.asesoriaActual}</span>}
            </p>
          </div>
          {estadoServidor.dryRun && (
            <Button
              variant="secondary"
              className="ml-auto shrink-0"
              onClick={handleCancelarSimulacion}
              disabled={cancelando || estadoServidor.cancelacionSolicitada}
            >
              <XCircle className="w-4 h-4 mr-2" />
              {cancelando || estadoServidor.cancelacionSolicitada ? 'Cancelando...' : 'Cancelar simulación'}
            </Button>
          )}
        </div>
      )}

      {/* Formulario de Parámetros y Acciones */}
      <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">NITs de empresa (opcional)</label>
            <input
              type="text"
              value={empresaIdsInput}
              onChange={(e) => setEmpresaIdsInput(e.target.value)}
              disabled={isProcessing || enCurso}
              placeholder="Ej: 30346025,900123456"
              className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
            />
            <span className="text-[11px] text-gray-400 px-1">Dejar en blanco para evaluar todas las empresas</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">Límite de asesorías (opcional)</label>
            <input
              type="number"
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              disabled={isProcessing || enCurso}
              placeholder="Ej: 20"
              className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
            />
            <span className="text-[11px] text-gray-400 px-1">Útil para pruebas en lotes pequeños</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input
            ref={inputImportarRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && void handleImportar(e.target.files[0])}
          />
          <Button variant="secondary" onClick={() => inputImportarRef.current?.click()} disabled={isProcessing || enCurso}>
            <Upload className="w-4 h-4 mr-2" />
            Importar actas (JSON)
          </Button>
          {asesoriaIdsImportadas ? (
            <span className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-900">
              Solo se procesarán <strong className="tabular-nums">{asesoriaIdsImportadas.length}</strong> asesorías importadas
              <button
                onClick={() => setAsesoriaIdsImportadas(null)}
                disabled={isProcessing || enCurso}
                className="font-semibold underline hover:text-amber-700 disabled:opacity-50"
              >
                Quitar
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-gray-400">Sin importar se evalúan todas las actas (o las de los NITs indicados)</span>
          )}
        </div>

        <div className="pt-2 flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-gray-100">
          <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              disabled={isProcessing || enCurso}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <span>
              <strong>Modo Simulación (Dry Run):</strong> no modifica Google Docs, solo audita y reporta qué se aplicaría.
            </span>
          </label>

          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handlePrevisualizar} disabled={isProcessing || enCurso || cargandoEstado}>
              <Search className="w-4 h-4 mr-2" />
              Previsualizar Lista
            </Button>

            <Button
              variant={dryRun ? 'primary' : 'danger'}
              onClick={() => handleIniciarBatch(!dryRun)}
              disabled={isProcessing || enCurso || cargandoEstado}
            >
              {isProcessing || enCurso ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : dryRun ? (
                <Play className="w-4 h-4 mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              {isProcessing || enCurso ? 'Ejecutando en servidor...' : dryRun ? 'Ejecutar Simulación (SSE)' : 'Aplicar en Google Docs Real'}
            </Button>
          </div>
        </div>

        {errorPreview && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{errorPreview}</span>
          </div>
        )}
      </div>

      {/* Consola de logs en tiempo real (SSE) */}
      {(isProcessing || enCurso) && (
        <JobProgressConsole progress={progress} logs={logs} isLoadingMore={isLoadingMore} hasMore={hasMore} onLoadMore={loadMoreLogs} />
      )}

      {/* Corrección de letra: tablas de audios escritas antes del arreglo quedaron en tamaño 1 */}
      <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900 block">Corregir letra de tablas de audios</span>
          Busca las actas cuya tabla de audios quedó con letra tamaño 1 y la deja en 10. No agrega filas ni cambia el texto; usa los mismos
          filtros de NIT y límite.
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="secondary"
            onClick={() => handleIniciarBatch(false, 'CORREGIR_LETRA')}
            disabled={isProcessing || enCurso || cargandoEstado}
          >
            <Search className="w-4 h-4 mr-2" />
            Revisar
          </Button>
          <Button
            variant="primary"
            onClick={() => handleIniciarBatch(true, 'CORREGIR_LETRA')}
            disabled={isProcessing || enCurso || cargandoEstado}
          >
            <Type className="w-4 h-4 mr-2" />
            Corregir letra
          </Button>
        </div>
      </div>

      {/* Métricas y Tarjetas de Resumen */}
      {resumenActual && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <span className="text-xs text-gray-500 font-medium block">Total Analizadas</span>
              <span className="text-2xl font-bold text-gray-900 mt-1 block">{resumenActual.totalAnalizadas}</span>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm">
              <span className="text-xs text-emerald-800 font-semibold block">
                {resumenActual.dryRun ? 'Elegibles Simulación' : 'Horas Aplicadas'}
              </span>
              <span className="text-2xl font-bold text-emerald-700 mt-1 block">
                {resumenActual.dryRun ? resumenActual.dryRunOk : resumenActual.aplicadas}
              </span>
              {resumenActual.sinAudiosTotalCero > 0 && (
                <span className="text-[11px] text-emerald-600">{resumenActual.sinAudiosTotalCero} con total 0s</span>
              )}
            </div>

            <div className="p-4 bg-rose-50 rounded-xl border border-rose-200 shadow-sm">
              <span className="text-xs text-rose-800 font-semibold flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" />
                Problema Audios Ajenos
              </span>
              <span className="text-2xl font-bold text-rose-700 mt-1 block">{resumenActual.saltadasProblemaAudios}</span>
              <span className="text-[11px] text-rose-600">Omitidas por seguridad</span>
            </div>

            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 shadow-sm">
              <span className="text-xs text-blue-800 font-medium block">Ya tenían AUDIOS</span>
              <span className="text-2xl font-bold text-blue-700 mt-1 block">{resumenActual.saltadasYaTenianAudios}</span>
              <span className="text-[11px] text-blue-600">No requieren cambios</span>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
              <span className="text-xs text-gray-600 font-medium block">No transformadas</span>
              <span className="text-2xl font-bold text-gray-700 mt-1 block">{resumenActual.saltadasNoTransformadas}</span>
              <span className="text-[11px] text-gray-500">Formato antiguo</span>
            </div>
          </div>

          {/* Tabla de detalle de actas analizadas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
                <button
                  onClick={() => setFiltroTabla('todos')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'todos' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Todas ({resumenActual.detalles.length})
                </button>
                <button
                  onClick={() => setFiltroTabla('elegibles')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'elegibles' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                  }`}
                >
                  Elegibles / Aplicadas ({resumenActual.dryRun ? resumenActual.dryRunOk : resumenActual.aplicadas})
                </button>
                <button
                  onClick={() => setFiltroTabla('problemas')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'problemas' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-800 hover:bg-rose-100'
                  }`}
                >
                  Problemas de Audios ({resumenActual.saltadasProblemaAudios})
                </button>
                <button
                  onClick={() => setFiltroTabla('ya_tienen')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'ya_tienen' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                  }`}
                >
                  Ya con Audios ({resumenActual.saltadasYaTenianAudios})
                </button>
                <button
                  onClick={() => setFiltroTabla('no_transformadas')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'no_transformadas' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Formato Antiguo ({resumenActual.saltadasNoTransformadas})
                </button>
                <button
                  onClick={() => setFiltroTabla('letra')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'letra' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                  }`}
                >
                  Letra tamaño 1 ({resumenActual.letraCorregida ?? 0})
                </button>
                <button
                  onClick={() => setFiltroTabla('errores')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    filtroTabla === 'errores' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-800 hover:bg-red-100'
                  }`}
                >
                  Errores ({resumenActual.fallidas})
                </button>
              </div>

              <Button variant="secondary" onClick={handleExportar} disabled={detallesFiltrados.length === 0} className="shrink-0">
                <Download className="w-4 h-4 mr-2" />
                Exportar ({detallesFiltrados.length})
              </Button>

              <div className="relative w-full md:w-64">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  value={busquedaTabla}
                  onChange={(e) => setBusquedaTabla(e.target.value)}
                  placeholder="Buscar empresa, asesor, ID..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-1.5 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-left text-xs text-gray-600">
                <thead className="bg-gray-50 text-gray-400 uppercase tracking-wider font-semibold sticky top-0 z-10 border-b border-gray-200">
                  <tr>
                    <th className="py-2.5 px-4">Asesoría</th>
                    <th className="py-2.5 px-4">Empresa / Asesor</th>
                    <th className="py-2.5 px-4">Estado</th>
                    <th className="py-2.5 px-4">Audios / Total</th>
                    <th className="py-2.5 px-4">Documento / Motivo</th>
                    <th className="py-2.5 px-4 text-right">Acta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detallesFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-400">
                        No hay asesorías para mostrar con el filtro seleccionado.
                      </td>
                    </tr>
                  ) : (
                    detallesFiltrados.map((item) => (
                      <tr key={item.asesoriaId} className="hover:bg-gray-50/80 transition-colors">
                        <td className="py-2.5 px-4 font-mono font-semibold text-gray-900">
                          #{item.asesoriaId}
                          {item.orden && <span className="text-gray-400 font-normal ml-1">({item.orden})</span>}
                        </td>

                        <td className="py-2.5 px-4">
                          <div className="font-semibold text-gray-800">{item.empresaNombre}</div>
                          <div className="text-[11px] text-gray-500">
                            NIT {item.empresaNit} · Dueño: {item.asesorNombre}
                          </div>
                        </td>

                        <td className="py-2.5 px-4">
                          {item.estado === 'APLICADA_OK' && <Badge variant="green">Fila Aplicada</Badge>}
                          {item.estado === 'DRY_RUN_OK' && <Badge variant="green">Elegible (Simulación)</Badge>}
                          {item.estado === 'SALTADA_PROBLEMA_AUDIOS' && <Badge variant="red">Audios de otro asesor</Badge>}
                          {item.estado === 'SALTADA_YA_TIENE_AUDIOS' && <Badge variant="blue">Ya tiene Audios</Badge>}
                          {item.estado === 'SALTADA_NO_TRANSFORMADA' && <Badge variant="gray">Formato Antiguo</Badge>}
                          {item.estado === 'SALTADA_SIN_EVIDENCIAS' && <Badge variant="warning">Sin Evidencias</Badge>}
                          {item.estado === 'SALTADA_SIN_TABLA_AUDIOS' && <Badge variant="gray">Sin tabla de audios</Badge>}
                          {item.estado === 'LETRA_POR_CORREGIR' && <Badge variant="warning">Letra tamaño 1</Badge>}
                          {item.estado === 'LETRA_CORREGIDA' && <Badge variant="green">Letra corregida</Badge>}
                          {item.estado === 'ERROR' && <Badge variant="red">Error</Badge>}
                        </td>

                        <td className="py-2.5 px-4">
                          <div className="font-medium text-gray-700">
                            {item.totalAudios} audio{item.totalAudios !== 1 ? 's' : ''}
                          </div>
                          <div className="text-[11px] text-gray-500 font-mono">{item.duracionTotal}</div>
                        </td>

                        <td className="py-2.5 px-4 max-w-md">
                          {item.docTitulo && <div className="font-mono text-[11px] text-gray-800 truncate">{item.docTitulo}</div>}
                          {item.motivo && (
                            <div
                              className={`text-[11px] mt-0.5 ${
                                item.estado === 'SALTADA_PROBLEMA_AUDIOS' ? 'text-rose-700 font-medium' : 'text-gray-500'
                              }`}
                            >
                              {item.motivo}
                            </div>
                          )}
                        </td>

                        <td className="py-2.5 px-4 text-right">
                          {item.actaUrl ? (
                            <a
                              href={item.actaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium hover:underline text-xs"
                            >
                              Ver <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
